import React, { useEffect } from "react";
import { useFlashboardConfigData } from "../hooks/useFlashboardConfigData";
import {
    Box,
    Button,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
} from "@material-ui/core";
import { ObjectiveForm } from "./ObjectiveForm";
import { Alert } from "@material-ui/lab";
import { toast, ToastContainer } from "react-toastify";
import { AtCircularProgress } from "../../../../components/AtCircularProgress";
import { api_host } from "../../../../common/globals/constants";
import { toBase64 } from "../../../../utils/get-base-64";
import { LocalImageUpload } from "../../../../components/LocalImageUpload";
import HelpIcon from "@material-ui/icons/Help";
import { ModalUmbralPasos } from "./ModalUmbralPasos";
import FlashboardConfigFilter from "./FlashboardConfigFilter";
import BtnConfiguracionColores from "../../ThresholdConfig/components/BtnConfiguracionColores";
import { withTranslation } from 'react-i18next';

export const ObjectivesList = withTranslation()(function ObjectivesList({ t }) {
    const {
        data,
        isLoading,
        isError,
        error,
        refetch,
    } = useFlashboardConfigData();
    const [copyData, setCopyData] = React.useState([]);
    const [isSendingData, setIsSendingData] = React.useState(false);
    const [title, setTitle] = React.useState(data.info.titulo);
    const [logo, setLogo] = React.useState(null);
    const [pasosObjetivoEditando, setPasosObjetivoEditando] = React.useState(
        null
    );
    const [search, setSearch] = React.useState("");
    const [isFirstRender, setIsFirstRender] = React.useState(true);

    useEffect(() => {
        if (!isLoading) {
            setTitle(data.info.titulo);
            setLogo(data.info.imagen);
            setCopyData(data.objetivos);
            setIsFirstRender(false);
        }
    }, [isLoading]);

    async function submitForm(event) {
        event.preventDefault();
        setIsSendingData(true);
        try {
            let objetivos = event.target.elements.objetivoId;
            let umbrales = event.target.elements.umbral;
            let checkVisibilidad = event.target.elements.checkVisibilidad;
            const updatedFlashBoardData = [];
            if (!objetivos.length) {
                objetivos = [objetivos];
                umbrales = [umbrales];
                checkVisibilidad = [checkVisibilidad];
            }
            for (let i = 0; i < objetivos.length; i++) {
                const detalleObjetivo = copyData.find(
                    (item) => item.objetivo_id === parseInt(objetivos[i].value)
                );
                updatedFlashBoardData.push({
                    objetivo_id: objetivos[i].value,
                    umbral_rendimiento: umbrales[i]?.value ?? 30,
                    visible: checkVisibilidad[i]?.checked ?? false,
                    link: "",
                    pasos: detalleObjetivo?.pasos ?? undefined,
                });
            }

            const token = sessionStorage.getItem("key");
            const clienteId = sessionStorage.getItem("client_id");
            const response = await toast.promise(
                fetch(
                    `${api_host}/reporte/online/indicadores/updatePanelExtraFlashboard/`,
                    {
                        method: "POST",
                        body: JSON.stringify({
                            token,
                            datos_flashboard: {
                                info: {
                                    titulo: title,
                                    imagen: logo ?? "",
                                    cliente_id: clienteId,
                                },
                                objetivos: updatedFlashBoardData,
                            },
                        }),
                        headers: { "Content-Type": "application/json" },
                    }
                ),
                {
                    pending: t('flashboard.toast.pending'),
                }
            );
            if (!response.ok) {
                throw new Error("Error al guardar los datos del form");
            }
            toast.success(t('flashboard.toast.saveSuccess'));
            refetch();
        } catch (err) {
            toast.error(err.message ?? t('flashboard.toast.retryLater'));
        } finally {
            setIsSendingData(false);
        }
    }

    async function selectFile(file) {
        const logoBase64 = await toBase64(file);
        setLogo(logoBase64);
    }

    function deleteImage() {
        setLogo(null);
    }

    function handleClickOpenModalPasos(objetivo) {
        setPasosObjetivoEditando(objetivo);
    }

    function closeModalPasos() {
        setPasosObjetivoEditando(null);
    }

    function updatePasosSuccess(objetivoId, pasos) {
        let updateCopyData = copyData;
        const index = updateCopyData.findIndex(
            (item) => objetivoId === item.objetivo_id
        );
        updateCopyData[index]["pasos"] = pasos;
        setCopyData(updateCopyData);
        closeModalPasos();
    }

    const updateFilter = React.useCallback((search) => {
        setSearch(search);
    }, []);

    const filteredData = React.useMemo(() => {
        const filteredData =
            search === ""
                ? copyData
                : copyData.filter((item) =>
                      item.nombre_objetivo
                          .toLowerCase()
                          .includes(search.toLowerCase())
                  );
        return filteredData;
    }, [search, copyData]);

    if (isLoading && isFirstRender) {
        return (
            <Box>
                <AtCircularProgress message={t('flashboard.loading.info')} />
            </Box>
        );
    }

    if (isError) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            {isLoading && (
                <Box>
                    <AtCircularProgress message={t('flashboard.loading.info')} />
                </Box>
            )}
            <BtnConfiguracionColores />
            <form onSubmit={submitForm}>
                <Grid container>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Box display="flex">
                            <LocalImageUpload
                                logo={logo}
                                selectFile={selectFile}
                                deleteImage={deleteImage}
                            />
                            <Tooltip
                                title={t('flashboard.help.tooltip')}
                            >
                                <IconButton
                                    onClick={(e) => e.preventDefault()}
                                    size="small"
                                >
                                    <HelpIcon style={{ color: "#7786fc" }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Grid>
                    <Grid item lg={3} md={5} sm={12} xs={12}>
                        <TextField
                            variant="outlined"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            label={t('flashboard.list.titleLabel')}
                            size="small"
                            fullWidth
                            style={{
                                marginTop: 15,
                                marginBottom: 15,
                            }}
                            required
                        />
                    </Grid>
                </Grid>
                <FlashboardConfigFilter changeSearchFilter={updateFilter} />
                <Table className="at-custom-form-table">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('flashboard.table.header.objective')}</TableCell>
                            <TableCell>{t('flashboard.table.header.responseThreshold')}</TableCell>
                            <TableCell>{t('flashboard.table.header.visibility')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredData.map((item) => (
                            <ObjectiveForm
                                key={item.objetivo_id}
                                objetivo={item.nombre_objetivo}
                                defaultValueUmbral={item.umbral_rendimiento}
                                objetivoId={item.objetivo_id}
                                visible={item.visible}
                                xmlBool={item.xml_config}
                                pasos={item.pasos}
                                openModalPasos={() =>
                                    handleClickOpenModalPasos(item)
                                }
                            />
                        ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell
                                colSpan={4}
                                align="right"
                                style={{ border: "none" }}
                            >
                                <Button
                                    type="submit"
                                    color="primary"
                                    variant="contained"
                                    disabled={isSendingData || isLoading}
                                >
                                    {t('flashboard.button.save')}
                                </Button>
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
                <ToastContainer />
            </form>
            {pasosObjetivoEditando && (
                <ModalUmbralPasos
                    objetivo={pasosObjetivoEditando}
                    open={Boolean(pasosObjetivoEditando)}
                    onClose={closeModalPasos}
                    updatePasosSuccess={updatePasosSuccess}
                />
            )}
        </Box>
    );
});
